import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";
import clsx from "clsx";

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  error?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export const Input = forwardRef<HTMLInputElement, Props>(function Input(
  { label, hint, error, leftIcon, rightIcon, className, id, ...rest },
  ref,
) {
  const inputId = id ?? rest.name;
  return (
    <div className="w-full">
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium text-surface-800 mb-1.5">
          {label}
        </label>
      )}
      <div className="relative">
        {leftIcon && (
          <span className="absolute inset-y-0 left-3 flex items-center text-surface-400 pointer-events-none">
            {leftIcon}
          </span>
        )}
        <input
          ref={ref}
          id={inputId}
          className={twMerge(clsx(
            "w-full h-10 bg-white border border-surface-300 rounded-lg text-surface-900",
            "placeholder:text-surface-400 transition-colors",
            "hover:border-surface-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/40",
            "disabled:bg-surface-50 disabled:cursor-not-allowed",
            leftIcon  ? "pl-10" : "pl-3.5",
            rightIcon ? "pr-10" : "pr-3.5",
            error && "border-danger-500 focus:border-danger-500 focus:ring-danger-400/40",
            className,
          ))}
          {...rest}
        />
        {rightIcon && (
          <span className="absolute inset-y-0 right-3 flex items-center text-surface-400">
            {rightIcon}
          </span>
        )}
      </div>
      {error && <p className="mt-1.5 text-xs text-danger-600">{error}</p>}
      {hint && !error && <p className="mt-1.5 text-xs text-surface-500">{hint}</p>}
    </div>
  );
});
